// The multi-selection panel: the properties every selected object has
// (commonProps.ts), each described once per kind and edited for the whole
// selection in one undo step; a polygon pair's booleans. docs/claude/selection.md.
import {
  NO_FINISH,
  roomFinishOf,
  setRoomFinish,
  takesFinish,
} from '../../shared/finishes';
import {
  MULTI_KINDS,
  selectedCount,
  selectedOf,
} from '../selection';
import {
  type CeilingShape,
  type Polygon,
  type RoomFinishes,
  type RoomWoods,
  type Vec2,
  type Wall,
} from '../../shared/types';
import {
  ceilingSlatsOf,
  setRoomWood,
  takesWood,
  wallSlatsOf,
  woodOf,
} from '../../shared/woods';
import {
  CEILING_SHAPE_OPTIONS,
  ceilingColorOf,
  ceilingShapeOf,
  EAVE_SHAPES,
  everyWallVertical,
  hasCeiling,
  hasSetbacks,
  resolveCeiling,
  setCeilingColor,
  setCeilingHeight,
  setCeilingShape,
  setCeilingThickness,
  setEaveHeight,
  setSetback,
  setSpringingHeight,
  SHAPE_REFUSED,
  shapeAvailability,
  springsInside,
  uniformSetback,
} from '../ceiling';
import {
  applyCommon,
  type CommonField,
  commonFields,
  commonValue,
  type PropField,
} from '../commonProps';
import {
  constraintGeom,
  constraintUi,
} from './constraintUi';
import {
  type ColorTarget,
  selectedColorTargets,
} from './copyColors';
import {
  beginGesture,
  commitGesture,
  flashStatus,
  flashToolStatus,
  invalidate,
  pushHistory,
  renderProperties,
  state,
} from './core';
import {
  wallThicknessOf,
  wallThicknessRange,
} from '../dent';
import {
  clampDoor,
  roomDoors,
} from '../door';
import {
  ceilingThicknessOf,
  DEFAULT_WALL_HEIGHT,
  externalHeight,
  objectHeight,
  setObjectHeight,
  wallBase,
} from '../heights';
import { darkenHex } from '../hexTone';
import { tr } from '../i18n';
import { isPolyRoom } from '../polyRoom';
import { newProductFromSelection } from './productEditor';
import {
  ANNOTATION_COLOR,
  COMMENT_FONT_MM,
  defaultWallColor,
  REVCLOUD_COLOR,
  TEXTBOX_CORNER_DEFAULT,
} from '../render';
import { rotateTextBoxTo } from '../textBox';
import {
  COMMENT_MAX_MM,
  COMMENT_MIN_MM,
  growTextBoxToFit,
} from './textBoxes';
import { canTilt } from '../tilt';
import { type KindObjects } from '../drawingObjects';
import { unreachable } from '../../shared/exhaustive';
import {
  appendRowSection,
  appendVariantRow,
  canEditStandards,
  editRoomTop,
  instanceBase,
  isPureInstanceMulti,
  isPurePolyMulti,
  regenerateSauna,
  resizeInstance,
  resizeLocked,
  runPolyBoolean,
  saunaOfHeater,
  selectedObjectIds,
  setTool,
  syncDimensions,
  tiltPart,
} from '../main';
import { ceilingNoteText } from './ceilingUi';
import { deleteAllSelected } from './deleteSelection';
import {
  CEILING_FINISH_OPTIONS,
  CEILING_SLAT_OPTIONS,
  ceilingFinishValue,
  FINISH_OPTIONS,
  NO_WOOD,
  WALL_SLAT_OPTIONS,
  WOOD_OPTIONS,
} from './roomCards';
import {
  categoryFields,
  runTakesHeightRow,
} from './categoryFields';
import {
  buttonRow,
  colorRow,
  deleteRow,
  infoRow,
  numberRow,
  opacityRow,
  sectionTitle,
  selectRow,
  showColorRow,
} from './panelRows';

/** A common field's key: what the property IS (commonProps.ts), so a polygon's
 *  outline, a free line's colour and an arc's stroke are all `stroke`. A
 *  category's field is `cat:<its id>`, a product's property `prop:<its key>`. */
type PropKey =
  | 'align'
  | 'boxHeight'
  | 'ceilingColor'
  | 'ceilingShape'
  | 'ceilingThickness'
  | 'cornerRadius'
  | 'depth'
  | 'eaveHeight'
  | 'fill'
  | 'fillOpacity'
  | 'finishCeiling'
  | 'finishWallsIn'
  | 'finishWallsOut'
  | 'height'
  | 'heightFromGround'
  | 'lineWidth'
  | 'revision'
  | 'rotation'
  | 'rotationX'
  | 'rotationY'
  | 'setback'
  | 'slatsCeiling'
  | 'slatsIn'
  | 'slatsOut'
  | 'springingHeight'
  | 'stroke'
  | 'strokeOpacity'
  | 'textColor'
  | 'textHeight'
  | 'thickness'
  | 'wallColor'
  | 'wallThickness'
  | 'width'
  | 'woodBenches'
  | 'woodCeiling'
  | 'woodWallsIn'
  | 'woodWallsOut'
  | `cat:${string}`
  | `prop:${string}`;

/** Every stored property of each kind a multi-selection holds, and the common
 *  fields whose rows in `selectionPropFields` edit it — or null: no row there
 *  (what places, shapes, names or holds it, what only its own panel or tools
 *  edit). A category's fields are `cat:*`, a product's properties `prop:*`. A
 *  Record over each type's keys, so a property added to the type does not
 *  compile until it says which row edits it, or that none does; a unit case
 *  holds the rows of each kind's case to what it says. */
export const MULTI_ROWS: { [K in ColorTarget['kind']]: Record<keyof KindObjects[K], readonly PropKey[] | null> } = {
  arc: {
    center: null,
    endDeg: null,
    groupId: null,
    id: null,
    layer: null,
    radius: null,
    startDeg: null,
    stroke: ['stroke'],
    strokeWidth: ['lineWidth'],
  },
  cloud: {
    color: ['stroke'],
    groupId: null,
    id: null,
    layer: null,
    points: null,
    revisionId: ['revision'],
    scallopMm: null,
  },
  instance: {
    autoBenches: null,
    backOnWall: null,
    backrestCuts: null,
    backrests: null,
    benchColor: null,
    benchCornerJoins: null,
    benchEdits: null,
    benchFrameSwaps: null,
    benchHoles: null,
    benchModel: null,
    benchRest: null,
    benchSupports: null,
    benchTierHeights: null,
    benchTiers: null,
    catalogHeightFromGround: null,
    category: null,
    ceiling: ['ceilingColor', 'ceilingShape', 'eaveHeight', 'springingHeight', 'setback', 'ceilingThickness'],
    closeEnd: null,
    closeStart: null,
    color: ['fill'],
    cubePos: null,
    cubeSnap: null,
    cutsBackrest: null,
    depth: ['depth'],
    door: null,
    doors: null,
    doserModel: null,
    drawPolygons: null,
    drawWalls: null,
    electrical: null,
    enteredName: null,
    fanModel: null,
    finishes: ['finishWallsIn', 'finishWallsOut', 'finishCeiling'],
    floorGrille: null,
    floorGrilleCuts: null,
    frozen: null,
    gaps: null,
    geometry: null,
    glass: null,
    glassHeight: null,
    groupId: null,
    groupName: null,
    heaterCover: ['cat:*'],
    heaterCoverColor: ['cat:*'],
    heaterGap: ['cat:*'],
    heaterWalls: null,
    height: ['height'],
    heightFromGround: ['heightFromGround'],
    hotTubModel: null,
    id: null,
    imageDataUrl: null,
    innerWalls: null,
    inWall: null,
    junctionBox: null,
    layer: null,
    lights: null,
    lockResize: null,
    model3d: null,
    name: null,
    nameI18n: null,
    nodes: null,
    openings: null,
    overlap: null,
    partId: null,
    polyBenches: null,
    polyBenchJoins: null,
    polygon: null,
    polygonConstraints: null,
    polygonCornerSlats: null,
    polygonEdgeLengths: null,
    polygonJoints: null,
    polygonRadii: null,
    polygonRelations: null,
    ports: null,
    powerPerMeter: ['cat:*'],
    powerPerSqm: ['cat:*'],
    preparations: null,
    properties: ['prop:*'],
    relId: null,
    roomAnchor: null,
    roomFixture: null,
    roomWallCut: null,
    rotation: ['rotation'],
    rotationX: ['rotationX'],
    rotationY: ['rotationY'],
    sculptedBenches: null,
    skirting: null,
    skirtingBoards: null,
    slatPresetId: null,
    slats: null,
    slatThickness: null,
    templateId: null,
    throughWall: null,
    variant: null, // its own row below the fields (appendVariantRow)
    wallColor: ['wallColor'],
    wallCut: null,
    wallRisers: null,
    wallThickness: ['wallThickness'],
    width: ['width'],
    woods: ['woodWallsIn', 'slatsIn', 'woodWallsOut', 'slatsOut', 'woodCeiling', 'slatsCeiling', 'woodBenches'],
    x: null,
    y: null,
  },
  leader: {
    color: ['stroke'],
    groupId: null,
    id: null,
    layer: null,
    points: null,
    text: null,
  },
  polygon: {
    edgeConstraints: null,
    edgeLengths: null,
    ellipse: null,
    fill: ['fill'],
    fillOpacity: ['fillOpacity'],
    groupId: null,
    height: ['height'],
    heightFromGround: ['heightFromGround'],
    holes: null,
    id: null,
    layer: null,
    name: null,
    points: ['width', 'depth'],
    preparations: null,
    relations: null,
    stroke: ['stroke'],
    strokeOpacity: ['strokeOpacity'],
    z: null,
  },
  text: {
    color: ['stroke'],
    cornerRadius: ['cornerRadius'],
    fill: ['fill'],
    fillOpacity: ['fillOpacity'],
    groupId: null,
    h: ['boxHeight'],
    heightMm: ['textHeight'],
    id: null,
    layer: null,
    leader: null,
    rotation: ['rotation'],
    strokeOpacity: ['strokeOpacity'],
    text: null,
    textColor: ['textColor'],
    w: ['width'],
    x: null,
    y: null,
  },
  wall: {
    align: ['align'],
    features: null,
    fill: ['fill', 'stroke'], // a free line's colour is its line's
    fillOpacity: ['fillOpacity', 'strokeOpacity'],
    groupId: null,
    height: ['height'],
    heightFromGround: ['heightFromGround'],
    id: null,
    layer: null,
    name: null,
    points: null,
    segLengths: null,
    stroke: ['stroke'],
    strokeOpacity: ['strokeOpacity'],
    style: null,
    thickness: ['thickness', 'lineWidth'],
    z: null,
  },
};

/** The properties one selected object's own panel edits, described for the
 *  multi-selection panel (commonProps.ts), which shows only those EVERY selected
 *  object has. A key names what a property IS, so a polygon's outline, a free
 *  line's colour and an arc's stroke are all `stroke`, and a heater's, a wall's
 *  and a room's height are all `height`. Setters write the value alone — the
 *  panel records one undo step for the whole selection — plus whatever the
 *  object's own panel does after (a room re-wraps its benches). */
function selectionPropFields(t: ColorTarget): PropField[] {
  const out: Array<PropField & { key: PropKey }> = [];
  const num = (key: PropKey, section: string, label: string, get: () => number, set: (v: number) => string | null | void): void => {
    out.push({ key, section, label, kind: 'number', get, set: (v) => set(Number(v)) });
  };
  const col = (key: PropKey, section: string, label: string, get: () => string, set: (hex: string) => void): void => {
    out.push({ key, section, label, kind: 'color', get, set: (v) => set(String(v)) });
  };
  const opa = (key: PropKey, label: string, get: () => number, set: (v: number) => void): void => {
    out.push({ key, section: 'Appearance', label, kind: 'opacity', get, set: (v) => set(Number(v)) });
  };
  const fill = (get: () => string, set: (hex: string) => void): void => col('fill', 'Appearance', 'Fill colour', get, set);
  const fillOpacity = (get: () => number, set: (v: number) => void): void => opa('fillOpacity', 'Fill opacity', get, set);
  const stroke = (get: () => string, set: (hex: string) => void): void => col('stroke', 'Appearance', 'Line colour', get, set);
  const strokeOpacity = (get: () => number, set: (v: number) => void): void => opa('strokeOpacity', 'Line opacity', get, set);
  const lineWidth = (get: () => number, set: (v: number) => void): void => num('lineWidth', 'Appearance', 'Line width (mm)', get, (v) => set(Math.max(1, v)));
  const height = (get: () => number, set: (v: number) => void): void => num('height', '3D', 'Height (mm)', get, set);
  const fromGround = (get: () => number, set: (v: number) => void): void => num('heightFromGround', '3D', 'Height from ground (mm)', get, (v) => set(Math.round(v)));
  switch (t.kind) {
    case 'instance': {
      const i = t.obj;
      const node = !!(i.nodes && i.nodes.length);
      if (!node && !resizeLocked(i)) {
        num('width', 'Size', 'Width (mm)', () => i.width, (v) => { if (v > 0) resizeInstance(i, v, i.depth); });
        num('depth', 'Size', 'Depth (mm)', () => i.depth, (v) => { if (v > 0) resizeInstance(i, i.width, v); });
      }
      if (!node) num('rotation', 'Size', 'Rotation Z (°)', () => i.rotation, (v) => { i.rotation = v; });
      if (canTilt(i)) {
        num('rotationX', 'Size', 'Rotation X (°)', () => i.rotationX ?? 0, (v) => { if (Number.isFinite(v)) tiltPart(i, { rotationX: v }); });
        num('rotationY', 'Size', 'Rotation Y (°)', () => i.rotationY ?? 0, (v) => { if (Number.isFinite(v)) tiltPart(i, { rotationY: v }); });
      }
      if (i.autoBenches) {
        num('height', '3D', 'Height (mm)', () => externalHeight(i), (v) => { const msg = ceilingNoteText(editRoomTop(i, () => setCeilingHeight(i, v))); regenerateSauna(i); return msg; });
        fromGround(() => Math.round(instanceBase(i)), (v) => editRoomTop(i, () => { i.heightFromGround = v; }));
        col('wallColor', 'Colours', 'Walls', () => i.wallColor || defaultWallColor(i), (hex) => { i.wallColor = hex; });
        if (takesWood(i)) {
          // The Wood card's rows (appendRoomWoodCard); turning boards a room has no wood for changes nothing.
          const pick = (key: PropKey, label: string, options: ReadonlyArray<readonly [string, string]>, get: () => string, set: (v: string) => void): void => {
            out.push({ key, section: 'Wood', label, kind: 'select', options, get, set: (v) => set(String(v)) });
          };
          const wood = (key: PropKey, label: string, part: keyof RoomWoods, none: string): void =>
            pick(key, label, [[NO_WOOD, none], ...WOOD_OPTIONS], () => woodOf(i.woods?.[part]?.wood)?.id ?? NO_WOOD, (v) => setRoomWood(i, part, { wood: v === NO_WOOD ? '' : v }));
          wood('woodWallsIn', 'Walls inside', 'wallsIn', 'Colour (no wood)');
          pick('slatsIn', 'Slats inside', WALL_SLAT_OPTIONS, () => wallSlatsOf(i.woods, 'in'), (v) => setRoomWood(i, 'wallsIn', { slats: v }));
          wood('woodWallsOut', 'Walls outside', 'wallsOut', 'As inside');
          pick('slatsOut', 'Slats outside', WALL_SLAT_OPTIONS, () => wallSlatsOf(i.woods, 'out'), (v) => setRoomWood(i, 'wallsOut', { slats: v }));
          if (hasCeiling(i)) {
            wood('woodCeiling', 'Ceiling', 'ceiling', 'As the walls inside');
            pick('slatsCeiling', 'Ceiling slats', CEILING_SLAT_OPTIONS, () => ceilingSlatsOf(i.woods), (v) => setRoomWood(i, 'ceiling', { slats: v }));
          }
          wood('woodBenches', 'Benches', 'benches', 'Colour (no wood)');
        }
        if (takesFinish(i)) {
          // The Finish card's rows (appendRoomFinishCard).
          const finish = (key: PropKey, label: string, part: keyof RoomFinishes, options: ReadonlyArray<readonly [string, string]>, get: () => string): void => {
            out.push({ key, section: 'Finish', label, kind: 'select', options, get, set: (v) => setRoomFinish(i, part, v === 'walls' ? '' : String(v)) });
          };
          finish('finishWallsIn', 'Walls inside', 'wallsIn', FINISH_OPTIONS, () => roomFinishOf(i.finishes, 'wallsIn') ?? NO_FINISH);
          finish('finishWallsOut', 'Walls outside', 'wallsOut', FINISH_OPTIONS, () => roomFinishOf(i.finishes, 'wallsOut') ?? NO_FINISH);
          if (hasCeiling(i)) finish('finishCeiling', 'Ceiling', 'ceiling', CEILING_FINISH_OPTIONS, () => ceilingFinishValue(i.finishes));
        }
        if (hasCeiling(i)) {
          col('ceilingColor', 'Colours', 'Ceiling', () => ceilingColorOf(i, i.wallColor || defaultWallColor(i)), (hex) => setCeilingColor(i, hex));
          out.push({
            key: 'ceilingShape', section: 'Ceiling', label: 'Shape', kind: 'select', options: CEILING_SHAPE_OPTIONS,
            refuses: (v) => shapeAvailability(i, v as CeilingShape),
            refusedMsg: SHAPE_REFUSED,
            get: () => ceilingShapeOf(i),
            set: (v) => ceilingNoteText(editRoomTop(i, () => setCeilingShape(i, v as CeilingShape))),
          });
          // The OUTSIDE eave is a property of the shapes that store it: a flat
          // room, or a barrel / dome (their springing is another), has none.
          // A truncated pyramid whose every wall stands vertical has no eave line.
          if (EAVE_SHAPES.includes(ceilingShapeOf(i)) && !everyWallVertical(resolveCeiling(i))) num('eaveHeight', 'Ceiling', 'Eave height (mm)', () => resolveCeiling(i).eave, (v) => ceilingNoteText(editRoomTop(i, () => setEaveHeight(i, v))));
          // The INSIDE springing: a property of the barrel, the cove and the dome only
          // (a cove whose every wall stands up to its flat springs from none).
          if (springsInside(ceilingShapeOf(i)) && !everyWallVertical(resolveCeiling(i))) num('springingHeight', 'Ceiling', 'Springing height (mm)', () => resolveCeiling(i).springing ?? NaN, (v) => ceilingNoteText(editRoomTop(i, () => setSpringingHeight(i, v))));
          // A truncated pyramid's and a cove's own: '(mixed)' too when its walls differ.
          if (hasSetbacks(ceilingShapeOf(i))) {
            num('setback', 'Ceiling', 'Setback (mm)', () => uniformSetback(i), (v) => ceilingNoteText(editRoomTop(i, () => setSetback(i, v))));
            out[out.length - 1].mixed = () => !Number.isFinite(uniformSetback(i));
          }
          num('ceilingThickness', 'Ceiling', 'Ceiling thickness (mm)', () => ceilingThicknessOf(i), (v) => ceilingNoteText(editRoomTop(i, () => setCeilingThickness(i, v))));
        }
        const range = wallThicknessRange(i.category);
        num('wallThickness', 'Bench layout', 'Wall thickness (mm)', () => wallThicknessOf(i), (v) => {
          i.wallThickness = Math.max(range.min, Math.min(range.max, Math.round(v)));
          if (!isPolyRoom(i)) for (const d of roomDoors(i)) if (d.innerWallId == null) clampDoor(i, d);
          regenerateSauna(i);
        });
        break;
      }
      // A part's drawn colour; a heater's "Body colour" field is this same value.
      fill(() => i.color ?? '#cdd6e0', (hex) => { i.color = hex; });
      fromGround(() => Math.round(instanceBase(i)), (v) => { i.heightFromGround = v; });
      if (node && runTakesHeightRow(i.category)) height(() => objectHeight(i), (v) => setObjectHeight(i, v));
      for (const f of categoryFields(i.category)) {
        if (f.id === 'bodyColor') continue;
        const key: PropKey = f.setsHeight ? 'height' : `cat:${f.id}`;
        out.push({
          key, section: key === 'height' ? '3D' : f.section, label: f.label, kind: f.kind,
          get: () => f.iGet(i),
          set: (v) => { f.iSet(i, v); if (f.regenSauna) { const room = saunaOfHeater(i); if (room) regenerateSauna(room); } },
        });
      }
      for (const p of i.properties || []) {
        out.push({
          key: `prop:${p.key}`, section: 'Product properties', label: p.unit ? `${p.label} (${p.unit})` : p.label, kind: 'text',
          get: () => String(p.value ?? ''),
          set: (v) => { const raw = String(v), n = Number(raw); p.value = raw !== '' && !Number.isNaN(n) ? n : raw; },
        });
      }
      break;
    }
    case 'polygon': {
      const p = t.obj;
      if (!p.ellipse) {
        const box = (): { w: number; d: number; cx: number; cy: number } => {
          const xs = p.points.map((q) => q.x), ys = p.points.map((q) => q.y);
          const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
          return { w: x1 - x0, d: y1 - y0, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 };
        };
        const scale = (fx: number, fy: number): void => {
          const b = box();
          const s = (q: Vec2): Vec2 => ({ x: b.cx + (q.x - b.cx) * fx, y: b.cy + (q.y - b.cy) * fy });
          p.points = p.points.map(s);
          if (p.holes) p.holes = p.holes.map((h) => h.map(s));
        };
        num('width', 'Size', 'Width (mm)', () => Math.round(box().w), (v) => { const w = box().w; if (v > 0 && w > 0) scale(v / w, 1); });
        num('depth', 'Size', 'Depth (mm)', () => Math.round(box().d), (v) => { const d = box().d; if (v > 0 && d > 0) scale(1, v / d); });
      }
      fill(() => p.fill, (hex) => { p.fill = hex; });
      fillOpacity(() => p.fillOpacity, (v) => { p.fillOpacity = v; });
      stroke(() => p.stroke, (hex) => { p.stroke = hex; });
      strokeOpacity(() => p.strokeOpacity, (v) => { p.strokeOpacity = v; });
      height(() => Math.round(p.height ?? 0), (v) => { p.height = Math.max(0, Math.round(v)); });
      fromGround(() => Math.round(p.heightFromGround ?? 0), (v) => { p.heightFromGround = v; });
      break;
    }
    case 'wall': {
      const w = t.obj;
      if (w.style === 'line') {
        // A free line is all line: its colour and opacity ARE its line's.
        stroke(() => w.fill, (hex) => { w.fill = hex; });
        strokeOpacity(() => w.fillOpacity, (v) => { w.fillOpacity = v; });
        lineWidth(() => w.thickness, (v) => { w.thickness = v; });
        height(() => w.height ?? DEFAULT_WALL_HEIGHT, (v) => { w.height = Math.max(0, v); });
        fromGround(() => Math.round(wallBase(w)), (v) => { w.heightFromGround = v; });
        break;
      }
      num('thickness', 'Size', 'Thickness (mm)', () => w.thickness, (v) => { w.thickness = Math.max(1, v); });
      fill(() => w.fill, (hex) => { w.fill = hex; });
      fillOpacity(() => w.fillOpacity, (v) => { w.fillOpacity = v; });
      stroke(() => w.stroke ?? darkenHex(w.fill, 0.5), (hex) => { w.stroke = hex; });
      strokeOpacity(() => w.strokeOpacity ?? w.fillOpacity, (v) => { w.strokeOpacity = v; });
      height(() => w.height ?? DEFAULT_WALL_HEIGHT, (v) => { w.height = Math.max(0, v); });
      fromGround(() => Math.round(w.heightFromGround ?? 0), (v) => { w.heightFromGround = v; });
      out.push({
        key: 'align', section: 'Placement', label: 'Alignment', kind: 'select', options: WALL_ALIGN_OPTIONS,
        get: () => w.align, set: (v) => { w.align = v as Wall['align']; },
      });
      break;
    }
    case 'text': {
      const c = t.obj;
      num('width', 'Size', 'Width (mm)', () => c.w, (v) => { if (v > 0) c.w = v; });
      num('boxHeight', 'Size', 'Height (mm)', () => c.h, (v) => { if (v > 0) c.h = v; });
      num('rotation', 'Size', 'Rotation Z (°)', () => c.rotation ?? 0, (v) => { if (Number.isFinite(v)) rotateTextBoxTo(c, v); });
      fill(() => c.fill, (hex) => { c.fill = hex; });
      fillOpacity(() => c.fillOpacity, (v) => { c.fillOpacity = v; });
      stroke(() => c.color ?? ANNOTATION_COLOR, (hex) => { c.color = hex; });
      strokeOpacity(() => c.strokeOpacity ?? 1, (v) => { c.strokeOpacity = v; });
      num('cornerRadius', 'Appearance', 'Corner radius (mm)', () => c.cornerRadius ?? TEXTBOX_CORNER_DEFAULT, (v) => { c.cornerRadius = Math.max(0, v); });
      num('textHeight', 'Text', 'Text height (mm)', () => c.heightMm ?? COMMENT_FONT_MM, (v) => {
        if (!(v > 0)) return;
        c.heightMm = Math.max(COMMENT_MIN_MM, Math.min(COMMENT_MAX_MM, v));
        growTextBoxToFit(c);
      });
      col('textColor', 'Text', 'Text colour', () => c.textColor ?? ANNOTATION_COLOR, (hex) => { c.textColor = hex; });
      break;
    }
    case 'arc': {
      const a = t.obj;
      stroke(() => a.stroke, (hex) => { a.stroke = hex; });
      lineWidth(() => a.strokeWidth, (v) => { a.strokeWidth = v; });
      break;
    }
    case 'leader': {
      const l = t.obj;
      stroke(() => l.color ?? ANNOTATION_COLOR, (hex) => { l.color = hex; });
      break;
    }
    case 'cloud': {
      const c = t.obj;
      stroke(() => c.color ?? REVCLOUD_COLOR, (hex) => { c.color = hex; });
      const revs = state.drawing.revisions || [];
      if (revs.length) {
        out.push({
          key: 'revision', section: 'Revision', label: 'Revision', kind: 'select',
          options: revs.map((r) => [r.id, r.tag || '(untitled)'] as const),
          get: () => c.revisionId, set: (v) => { c.revisionId = String(v); },
        });
      }
      break;
    }
    default: unreachable(t, 'multi-selection kind');
  }
  return out;
}

const WALL_ALIGN_OPTIONS: ReadonlyArray<readonly [string, string]> = [['center', 'Centred'], ['left', 'Left of line'], ['right', 'Right of line']];

/** The shared fields as panel rows. A field the selection disagrees on shows
 *  "(mixed)" instead of one object's value; typing (or picking) a value writes it
 *  to every selected object as ONE undo step. */
function appendCommonFields(body: HTMLElement, common: CommonField[]): void {
  let section = '';
  const after = (): void => { syncDimensions(); renderProperties(); invalidate(); };
  // Written where it can be (one undo step, none when nothing changed); the bar
  // says how many objects could not take it, and why, then the first clamp.
  const refusal = (c: CommonField, n: number, why: string): string => {
    const msg = c.fields.find((f) => f.refusedMsg)?.refusedMsg;
    return msg ? tr(msg).replace('{n}', String(n)).replace('{reason}', tr(why)) : tr(why);
  };
  const write = (c: CommonField, v: number | string): void => {
    beginGesture();
    const done = applyCommon(c, v);
    commitGesture();
    after();
    if (done.refused && done.refusal) flashStatus(refusal(c, done.refused, done.refusal));
    if (done.reasons.length) flashStatus(done.reasons[0]);
  };
  // A colour write does not re-render, so the other colour rows are re-read
  // after it: one may show another's colour (the ceiling follows the walls).
  const colourRows: Array<{ c: CommonField; row: HTMLElement }> = [];
  const refreshColours = (written: CommonField): void => {
    for (const r of colourRows) if (r.c !== written) { const v = commonValue(r.c); showColorRow(r.row, v == null ? null : String(v)); }
  };
  for (const c of common) {
    if (c.section !== section) { body.appendChild(sectionTitle(c.section)); section = c.section; }
    const mixed = c.value == null;
    const first = c.fields[0].get();
    let row: HTMLElement;
    if (c.kind === 'color') {
      // colorRow records the history step itself; no re-render, so a picker drag
      // isn't torn down under the pointer.
      row = colorRow(c.label, String(mixed ? first : c.value), (hex) => { applyCommon(c, hex); refreshColours(c); });
      colourRows.push({ c, row });
      const hex = row.querySelector<HTMLInputElement>('input[type="text"]');
      if (mixed && hex) { hex.value = ''; hex.placeholder = tr('(mixed)'); }
    } else if (c.kind === 'opacity') {
      row = opacityRow(c.label, Number(mixed ? first : c.value), (v) => applyCommon(c, v));
      const out = row.querySelector('span');
      if (mixed && out) out.textContent = tr('(mixed)');
    } else if (c.kind === 'number') {
      row = numberRow(c.label, Number(mixed ? 0 : c.value), (v) => write(c, v), { commit: true });
      const input = row.querySelector('input');
      if (mixed && input) { input.value = ''; input.placeholder = tr('(mixed)'); }
    } else if (c.kind === 'select') {
      row = selectRow(c.label, c.options ?? [], String(mixed ? '' : c.value), (v) => write(c, v));
      const sel = row.querySelector('select');
      if (mixed && sel) {
        const opt = document.createElement('option');
        opt.value = ''; opt.disabled = true; opt.textContent = tr('(mixed)');
        sel.prepend(opt);
        sel.value = '';
      }
    } else {
      row = document.createElement('div');
      row.className = 'prop-row';
      const l = document.createElement('label');
      l.textContent = tr(c.label);
      const input = document.createElement('input');
      input.type = 'text';
      if (mixed) input.placeholder = tr('(mixed)'); else input.value = String(c.value);
      input.addEventListener('change', () => { pushHistory(); applyCommon(c, input.value); after(); });
      row.append(l, input);
    }
    body.appendChild(row);
  }
}

/** The panel for any selection of two or more objects, of one kind or many: the
 *  properties EVERY selected object has (commonProps.ts), each editing them all
 *  at once, then what only that selection offers — a repeat row's Flip, a new
 *  product from parts, the boolean operations on two polygons. */
export function renderMultiSelectionPanel(body: HTMLElement): void {
  const total = selectedCount(state, MULTI_KINDS);
  const pureInst = isPureInstanceMulti(), purePoly = isPurePolyMulti();
  const noun = pureInst ? tr('items selected') : purePoly ? tr('polygons selected') : tr('objects selected');
  body.appendChild(sectionTitle(`${total} ${noun}`));
  if (!pureInst && !purePoly) {
    const rows: Array<[string, number]> = [
      ['Parts', selectedOf(state, 'instance').length],
      ['Polygons', selectedOf(state, 'polygon').length],
      ['Walls & lines', selectedOf(state, 'wall').length],
      ['Texts', selectedOf(state, 'text').length],
      ['Arcs', selectedOf(state, 'arc').length],
      ['Leaders', selectedOf(state, 'leader').length],
      ['Revision clouds', selectedOf(state, 'cloud').length],
    ];
    for (const [label, n] of rows) if (n > 0) body.appendChild(infoRow(label, String(n)));
  }
  if (pureInst) {
    // A whole repeat row (a click selects the row's group) keeps its Row section.
    const insts = state.drawing.instances.filter((i) => selectedOf(state, 'instance').includes(i.id));
    if (insts[0]?.groupId && insts.every((i) => i.category === 'repeatable' && i.groupId === insts[0].groupId)) appendRowSection(body, insts[0]);
  }
  if (pureInst) appendVariantRow(body, state.drawing.instances.filter((i) => selectedOf(state, 'instance').includes(i.id)));
  const common = commonFields(selectedColorTargets().map(selectionPropFields));
  if (common.length) appendCommonFields(body, common);
  else {
    const e = document.createElement('div');
    e.className = 'prop-empty';
    e.textContent = tr('No properties in common.');
    body.appendChild(e);
  }
  if (pureInst && canEditStandards()) body.appendChild(buttonRow('＋ New product from selection', () => void newProductFromSelection()));
  const polys = purePoly ? (state.drawing.polygons || []).filter((p) => selectedOf(state, 'polygon').includes(p.id)) : [];
  if (polys.length === 2) appendPolyBooleans(body, polys[0], polys[1]);
  // Select two objects, then relate them (SolidWorks' Add Relation): the
  // Constrain tool armed with its picks limited to these two.
  const pair = [...selectedObjectIds()];
  if (pair.length === 2 && pair.every((id) => constraintGeom(id, false))) {
    body.appendChild(buttonRow('Add relation…', () => {
      setTool('constrain');
      constraintUi.constrainScope = new Set(pair);
      flashToolStatus(tr('Pick a vertex or edge of each of the two selected objects.'));
      invalidate();
    }));
  }
  body.appendChild(deleteRow(() => deleteAllSelected()));
  const hint = document.createElement('div');
  hint.className = 'prop-empty';
  hint.style.marginTop = '10px';
  hint.textContent = polys.length === 2 ? tr('Drag to move all · Boolean replaces both with the result.')
    : purePoly ? tr('Drag to move all.') : tr('Drag any selected object to move them all together.');
  body.appendChild(hint);
}

/** Boolean operations on exactly two polygons, in draw order: `back` is the back
 *  shape and `front` the front; difference is offered both ways since direction
 *  matters. */
function appendPolyBooleans(body: HTMLElement, back: Polygon, front: Polygon): void {
  body.appendChild(sectionTitle('Boolean'));
  const grid = document.createElement('div');
  grid.style.display = 'grid';
  grid.style.gridTemplateColumns = '1fr 1fr';
  grid.style.gap = '4px';
  grid.style.margin = '6px 0';
  const mk = (label: string, run: () => void): HTMLButtonElement => {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.fontSize = '12px';
    b.style.padding = '6px 4px';
    b.addEventListener('click', run);
    return b;
  };
  grid.append(
    mk(tr('Union'), () => runPolyBoolean('union', back.id, front.id)),
    mk(tr('Intersect'), () => runPolyBoolean('intersection', back.id, front.id)),
    mk(tr('Back − Front'), () => runPolyBoolean('difference', back.id, front.id)),
    mk(tr('Front − Back'), () => runPolyBoolean('difference', front.id, back.id)),
  );
  body.appendChild(grid);
}
